import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import TicketCard from './TicketCard';
import ProjectOwnerTicketModal from '../modals/ProjectOwnerTicketModal';
import useIsMobile from '../../hooks/useIsMobile';

const PortalAwareDraggable = ({ ticket, provided, snapshot, onTicketClick }) => {
  const usePortal = snapshot.isDragging;

  const child = (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      style={{
        ...provided.draggableProps.style,
        cursor: snapshot.isDragging ? 'grabbing' : 'grab',
      }}
      className={snapshot.isDragging ? 'is-dragging-class' : ''}
    >
      <TicketCard
        ticket={ticket}
        onClick={onTicketClick}
        isDragging={snapshot.isDragging}
      />
    </div>
  );

  if (!usePortal) {
    return child;
  }

  // If dragging, render the item in a portal
  return ReactDOM.createPortal(child, document.body);
};

const KanbanView = ({ tickets, onTicketClick, onDragEnd, onCreateTicket, selectedProject, projectStatuses }) => {
  const isMobile = useIsMobile();

  // Backend'den gelen status'lara göre dinamik column'lar oluştur
  const getColumns = () => {
    // 1. Eğer props ile projectStatuses gelmişse (en güvenilir kaynak)
    if (projectStatuses && projectStatuses.length > 0) {
      // show_on_board olanları al ve order_index'e göre sırala
      return [...projectStatuses]
        .filter(s => s.show_on_board !== false)
        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
        .map(s => s.status_name);
    }

    // 2. Yedek Plan: Ticket'ların içindeki unique status isimlerini al
    if (tickets && tickets.length > 0) {
      return [...new Set(tickets.map(ticket => ticket.status))];
    }

    // 3. Hiçbir şey yoksa varsayılanlar
    return ['Beklemede', 'Yapılıyor', 'Bitti'];
  };

  const columns = getColumns();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Her zaman yeni tasarımı kullan

  const handleCreateTicket = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleTicketCreated = (newTicket) => {
    onCreateTicket(newTicket);
    setIsCreateModalOpen(false);
  };

  // Drag & Drop işlemi
  const handleDragEnd = (result) => {
    // console.log('🔄 Drag & Drop Event:', result);              // Drag & Drop event'inin detaylarını görmek için

    const { destination, source, draggableId } = result;

    // Eğer geçerli bir hedef yoksa işlemi iptal et
    if (!destination) {
      // console.log('❌ No valid destination, drag cancelled');   // Geçersiz hedef durumunu görmek için
      return;
    }

    // Aynı yerde bırakıldıysa işlemi iptal et
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      // console.log('❌ Same position, drag cancelled');          // Aynı pozisyona bırakma durumunu görmek için
      return;
    }

    // Ticket'ın yeni status'unu bul
    const newStatus = destination.droppableId;
    // console.log(`✅ Dragging ticket ${draggableId} from ${source.droppableId} to ${newStatus}`);  // Ticket sürükleme işlemini takip etmek için

    // onDragEnd callback'ini çağır (parent component'te implement edilecek)
    if (onDragEnd) {
      // console.log('📞 Calling onDragEnd callback');              // Callback çağrısını takip etmek için
      onDragEnd(draggableId, newStatus);
    } else {
      // console.log('❌ onDragEnd callback not provided');        // Callback eksikliğini tespit etmek için
    }
  };

  // Drag & Drop başlangıcında
  const handleDragStart = (result) => {
    // console.log('🚀 Drag started:', result);                    // Drag başlangıcını takip etmek için
  };

  // Drag & Drop sırasında
  const handleDragUpdate = (result) => {
    // console.log('📍 Drag update:', result);                     // Drag güncellemelerini takip etmek için
  };

  // Status adlarını daha güzel göster
  const getStatusDisplayName = (status) => {
    const statusMap = {
      'Beklemede': 'Beklemede',
      'Yapılıyor': 'Yapılıyor',
      'Bitti': 'Bitti',
      'To Do': 'To Do',
      'In Progress': 'In Progress',
      'Done': 'Done',
      'Review': 'Review',
      'Cancelled': 'Cancelled'
    };
    return statusMap[status] || status;
  };

  // Status renklerini belirle - tek ana renk
  const getStatusColor = (status) => {
    return 'from-blue-50 to-blue-100 border-blue-200';
  };

  // console.log('🎯 KanbanView rendering with:', { tickets: tickets.length, columns });  // Component render bilgilerini takip etmek için

  return (
    <DragDropContext
      onDragStart={handleDragStart}
      onDragUpdate={handleDragUpdate}
      onDragEnd={handleDragEnd}
    >
      <div className="flex space-x-3 md:space-x-4 p-2 md:p-4 overflow-x-auto snap-x snap-mandatory">
        {columns.map(status => {
          const statusTickets = tickets.filter(t => t.status === status);
          const ticketCount = statusTickets.length;

          return (
            <div key={status} className={`flex-shrink-0 group/column snap-start ${isMobile ? 'w-[84vw]' : 'w-64'}`} style={{ height: isMobile ? '70vh' : '600px', maxHeight: isMobile ? '70vh' : '600px' }}>
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm flex flex-col h-full">
                {/* Header - sabit */}
                <div className={`bg-gradient-to-r ${getStatusColor(status)} dark:from-gray-700 dark:to-gray-800 rounded-t-lg p-2 border-b border-gray-200/50 dark:border-gray-600/50 flex-shrink-0`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                      {getStatusDisplayName(status)}
                    </h3>
                    <span className="bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full text-xs font-medium shadow-sm">
                      {ticketCount} ticket
                    </span>
                  </div>
                </div>

                {/* Scrollable ticket area */}
                <div className="flex-1 min-h-0 relative overflow-hidden">
                  <Droppable droppableId={status}>
                    {(provided, snapshot) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={`h-full p-2 pb-14 space-y-1.5 overflow-y-auto kanban-scroll transition-all duration-200 ${snapshot.isDraggingOver
                          ? 'bg-blue-50 dark:bg-blue-900/10 border-t-2 border-blue-200 dark:border-blue-600'
                          : 'bg-gray-50/50 dark:bg-gray-900/20'
                          }`}
                      >
                        {statusTickets.map((ticket, index) => (
                          <Draggable
                            key={ticket.id}
                            draggableId={String(ticket.id)}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <PortalAwareDraggable
                                ticket={ticket}
                                provided={provided}
                                snapshot={snapshot}
                                onTicketClick={onTicketClick}
                              />
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>

                {/* Create ticket button - sabit, column'un en altında */}
                <div className="p-2 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800 rounded-b-lg">
                  <button
                    onClick={handleCreateTicket}
                    className="w-full p-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded hover:border-gray-400 dark:hover:border-gray-500 transition-colors duration-200 group-hover/column:border-gray-400 dark:group-hover/column:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    + Yeni Ticket
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Ticket Modal - Yeni tasarım */}
      {isCreateModalOpen && (
        <ProjectOwnerTicketModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          onCreateTicket={handleTicketCreated}
          selectedProject={selectedProject}
        />
      )}
    </DragDropContext>
  );
};

export default KanbanView;
